import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

const FaqSection = () => {
  const { t } = useTranslation();

  const faqs = [
    {
      question: 'Funktioniert Salescockpit auch mit unserem ERP-System?',
      answer: 'Salescockpit ist speziell für Microsoft Business Central optimiert und bietet eine nahtlose, vollintegrierte Schnittstelle zu diesem System. Durch unser flexibles Import-Modul können wir jedoch auch andere ERP-Systeme wie SAP, Sage oder proALPHA anbinden. Je nach Anforderung realisieren wir den Datenexport als einfachen E-Mail-Checkout an den Innendienst oder als strukturierte Datei (XML, EDI usw.). Dank unseres modularen Systemaufbaus erfüllen wir problemlos auch kundenspezifische Integrationsanforderungen.'
    },
    {
      question: 'Brauchen wir eine konstante Internetverbindung für Salescockpit?',
      answer: 'Nein, eine konstante Internetverbindung ist nicht erforderlich. Salescockpit verfügt über einen leistungsstarken Offline-Modus, der es deinem Außendienst ermöglicht, auch ohne Internetverbindung zu arbeiten. Alle eingegebenen Daten werden lokal gespeichert und automatisch synchronisiert, sobald wieder eine Verbindung besteht.'
    },
    {
      question: 'Wie lange dauert die Einführung von Salescockpit?',
      answer: 'Die typische Implementierungsdauer beträgt 3-6 Wochen, abhängig von der Komplexität der ERP-Integration und spezifischen Anforderungen. Der Prozess umfasst Anforderungsanalyse, Konfiguration, Schulungen und eine begleitete Go-Live-Phase. Wir sorgen dafür, dass die Einführung schrittweise und ohne Unterbrechung deines Tagesgeschäfts erfolgt.'
    },
    {
      question: 'Wie werden unsere Mitarbeiter geschult?',
      answer: 'Wir bieten umfassende Schulungsprogramme für alle Benutzer-Ebenen: vom Endanwender im Außendienst bis zum Administrator. Die Schulungen können sowohl vor Ort als auch remote durchgeführt werden und beinhalten praktische Übungen mit realen Szenarien. Zusätzlich erhältst du Zugang zu einer umfangreichen Dokumentation und Video-Tutorials.'
    },
    {
      question: 'Welche Geräte werden von Salescockpit unterstützt?',
      answer: 'Salescockpit ist plattformübergreifend konzipiert und läuft auf praktisch allen modernen Geräten. Die App ist verfügbar für iOS und Android (Tablets). Dadurch kann dein Außendienst mit seinen bevorzugten Geräten arbeiten. Wir planen auch die Unterstützung für Smartphones und Desktop-Browsern in Zukunft.'
    },
    {
      question: 'Wie sicher sind unsere Daten in Salescockpit?',
      answer: 'Datensicherheit hat für uns höchste Priorität. Salescockpit nutzt Verschlüsselung auf Transportebene (TLS) sowie Datenverschlüsselung im Ruhezustand. Die Anwendung wird in zertifizierten deutschen Rechenzentren gehostet, die alle DSGVO-Anforderungen erfüllen. Zudem bieten wir rollenbasierte Zugriffskontrollen und detaillierte Audit-Logs.'
    },
    {
      question: 'Kann Salescockpit an unsere individuellen Anforderungen angepasst werden?',
      answer: 'Ja, Salescockpit ist hochgradig anpassbar. Je nach gewähltem Paket können wir individuelle Anpassungen vornehmen – von benutzerdefinierten Formularfeldern und Workflows bis hin zu speziellen Integrationen und individuellen Berichten. In der Enterprise-Version ist sogar ein komplett individuelles Branding (White-Label) möglich.'
    },
    {
      question: 'Wie erfolgt die Abrechnung? Gibt es versteckte Kosten?',
      answer: 'Wir bieten transparente Preismodelle mit monatlicher oder jährlicher Abrechnung (10% Rabatt bei jährlicher Zahlung). Die Kosten basieren auf der Anzahl der Benutzer und dem gewählten Funktionsumfang. Die einmaligen Implementierungskosten werden separat berechnet. Es gibt keine versteckten Kosten – Updates, Standardsupport und Hosting sind im Preis inbegriffen.'
    }
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Häufig gestellte Fragen zu Salescockpit
          </h2>
          <p className="text-xl text-gray-700">
            Hier findest du Antworten auf die wichtigsten Fragen rund um die digitale Auftragserfassung mit Salescockpit.
          </p>
        </div>

        <div className="max-w-4xl mx-auto divide-y divide-gray-200 rounded-xl overflow-hidden">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 hover:bg-blue-50 transition-colors duration-200">
              <button
                className="w-full px-8 py-6 text-left focus:outline-none"
                onClick={() => toggleFaq(index)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">{faq.question}</h3>
                  <span className={cn(
                    "ml-6 flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center",
                    "transition-transform duration-300",
                    openIndex === index ? "transform rotate-180" : ""
                  )}>
                    <svg className="h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </button>
              <div className={cn(
                "overflow-hidden transition-all duration-300 max-h-0",
                openIndex === index ? "max-h-96" : "max-h-0"
              )}>
                <div className="px-8 pb-6 text-gray-700">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-xl text-gray-700">
            Hast du weitere Fragen? Unser Expertenteam steht dir gerne zur Verfügung.
          </p>
          <a 
            href="#contact"
            className="inline-block mt-6 px-8 py-3 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Kontaktiere uns
          </a>
        </div>
      </div>
    </section>
  );
};

export default FaqSection; 